/**
 * L2: change volume per day, from the persisted change history.
 *
 * The live feed below only reaches back 48 hours, because that is all Resource
 * Graph's `resourcechanges` is polled for. The `resource_change` table has been
 * accumulating a rolling 30 days of it, so this is the same feed at the
 * altitude a trend needs — and it costs no Azure call.
 *
 * Two honesty rules shape it. A day the poller never wrote carries `null`, so
 * the line renders a **gap** rather than dropping to zero (a missed sweep is
 * not a quiet day), and every summary figure is computed over recorded days
 * only, with the coverage stated. And the series is estate-wide: the history
 * keeps no per-subscription split, so this panel sits above the filter row
 * rather than pretending to honour it.
 */

import { Chart } from "@norican/dataviz";
import { EmptyState, Statistic, Typography } from "@norican/react";

import { ChartCard } from "@/components/board";
import type { CloudChanges } from "@/lib/api";
import { formatCount } from "@/lib/charts/config";

import { PanelCard } from "./panel-card";
import { formatDay } from "./status";

const VOLUME_CONFIG = { total: { label: "Changes", color: "var(--norican-chart-1)" } };

interface ChangeVolumePanelProps {
  changes: CloudChanges;
}

export function ChangeVolumePanel({ changes }: ChangeVolumePanelProps) {
  const history = changes.history;

  if (history == null) {
    return (
      <PanelCard title="Change volume" caption="Unknown">
        <Typography variant="body-sm" tone="muted">
          The persisted change history could not be read, so the daily trend is unknown — not empty.
          The reason is listed under collection problems.
        </Typography>
      </PanelCard>
    );
  }

  const rows = history.days.map((day) => ({ ...day, label: formatDay(day.day) }));
  const coverage = `${formatCount(history.days_recorded)} of ${formatCount(
    history.window_days,
  )} days recorded`;

  return (
    <div className="flex flex-col gap-4">
      <PanelCard title="Change volume" caption={`Last ${history.window_days} days · ${coverage}`}>
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <Statistic
            label="Changes recorded"
            value={formatCount(history.total)}
            size="lg"
            caption={`Across the ${formatCount(history.days_recorded)} day(s) that hold data`}
          />
          <Statistic
            label="Deletes"
            value={formatCount(history.deletes)}
            tone={history.deletes > 0 ? "warning" : "neutral"}
            caption="The change most worth a second look"
          />
          <Statistic
            label="Mean per recorded day"
            value={history.mean_per_recorded_day == null ? "—" : `${history.mean_per_recorded_day}`}
            caption={
              history.mean_per_recorded_day == null
                ? "No day in the window holds data yet"
                : "Days with no data are excluded, not counted as zero"
            }
          />
          <Statistic
            label="Busiest day"
            value={history.busiest_day ? formatDay(history.busiest_day) : "—"}
            caption={
              history.busiest_day_total == null
                ? "No day recorded yet"
                : `${formatCount(history.busiest_day_total)} changes`
            }
          />
        </div>
      </PanelCard>

      <ChartCard
        title="Changes per day"
        caption={`${coverage} · estate-wide — the history keeps no per-subscription split`}
        since={changes.collected_at}
        tableTwin={{
          id: "change-volume",
          ariaLabel: "Resource changes recorded per day",
          data: rows.filter((row) => row.total != null),
          getRowId: (row) => row.day,
          columns: [
            { id: "day", header: "Day", accessor: (row) => row.label },
            {
              id: "total",
              header: "Changes",
              accessor: (row) => row.total,
              align: "end",
              cell: (row) => <span className="tabular-nums">{formatCount(row.total)}</span>,
            },
            {
              id: "creates",
              header: "Creates",
              accessor: (row) => row.creates,
              align: "end",
              cell: (row) => <span className="tabular-nums">{formatCount(row.creates)}</span>,
            },
            {
              id: "updates",
              header: "Updates",
              accessor: (row) => row.updates,
              align: "end",
              cell: (row) => <span className="tabular-nums">{formatCount(row.updates)}</span>,
            },
            {
              id: "deletes",
              header: "Deletes",
              accessor: (row) => row.deletes,
              align: "end",
              cell: (row) => <span className="tabular-nums">{formatCount(row.deletes)}</span>,
            },
            {
              id: "resources",
              header: "Resources touched",
              accessor: (row) => row.resources,
              align: "end",
              cell: (row) => <span className="tabular-nums">{formatCount(row.resources)}</span>,
            },
          ],
        }}
      >
        {history.days_recorded < 2 ? (
          <EmptyState
            size="sm"
            title="Not enough history for a trend yet"
            description="The poller has to record at least two days of changes before a line means anything."
          />
        ) : (
          <div className="flex flex-col gap-2">
            <Chart.Container
              config={VOLUME_CONFIG}
              className="h-64"
              aria-label={`Resource changes recorded per day, last ${history.window_days} days`}
            >
              <Chart.LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                <Chart.CartesianGrid {...Chart.gridProps} vertical={false} />
                <Chart.XAxis {...Chart.axisProps} dataKey="label" />
                <Chart.YAxis {...Chart.axisProps} width={40} allowDecimals={false} />
                <Chart.Tooltip content={<Chart.TooltipContent />} />
                <Chart.Line
                  {...Chart.seriesDefaults}
                  dataKey="total"
                  type="monotone"
                  stroke="var(--color-total)"
                  strokeWidth={2}
                  dot={false}
                />
              </Chart.LineChart>
            </Chart.Container>
            <Typography variant="body-sm" tone="muted">
              A break in the line is a day the poller recorded nothing — a gap in collection, not a
              day with no changes.
            </Typography>
          </div>
        )}
      </ChartCard>
    </div>
  );
}
